import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { eventsService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
    HiOutlineCalendar,
    HiOutlineClock,
    HiOutlineLocationMarker,
    HiOutlineUserGroup,
    HiOutlinePencil,
    HiOutlineTrash,
    HiOutlineDocument,
    HiOutlinePlus,
    HiOutlineX,
    HiOutlineCheckCircle,
    HiOutlineInformationCircle
} from 'react-icons/hi';
import './EventsManage.css';

const MEDIA_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');

const EventsManage = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [documentPreview, setDocumentPreview] = useState(null);
    useAuth();
    
    const [formData, setFormData] = useState({
        title_es: '',
        description_es: '',
        location_es: '',
        event_date: '',
        event_time: '',
        end_date: '',
        featured_image: null,
        document_url: null,
        category: '',
        registration_url: '',
        max_participants: '',
        status: 'upcoming',
        is_active: true
    });

    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            const response = await eventsService.getAll();
            setEvents(response.data);
        } catch (error) {
            toast.error('Error al cargar eventos');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (files && files[0]) {
            setFormData(prev => ({
                ...prev,
                [name]: files[0]
            }));

            if (name === 'featured_image' && files[0].type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(files[0]);
            }

            if (name === 'document_url') {
                setDocumentPreview(files[0].name);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const submitData = new FormData();
            
            Object.keys(formData).forEach(key => {
                if (key !== 'featured_image' && key !== 'document_url' && formData[key] !== null && formData[key] !== '') {
                    submitData.append(key, formData[key]);
                }
            });

            if (formData.featured_image) {
                submitData.append('featured_image', formData.featured_image);
            }
            if (formData.document_url) {
                submitData.append('document_url', formData.document_url);
            }

            if (editingEvent) {
                await eventsService.update(editingEvent.id, submitData);
                toast.success('Evento actualizado exitosamente');
            } else {
                await eventsService.create(submitData);
                toast.success('Evento creado exitosamente');
            }

            resetForm();
            loadEvents();
            setShowModal(false);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al guardar evento');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (event) => {
        setEditingEvent(event);
        setFormData({
            title_es: event.title_es,
            description_es: event.description_es,
            location_es: event.location_es || '',
            event_date: event.event_date?.split('T')[0] || '',
            event_time: event.event_time || '',
            end_date: event.end_date?.split('T')[0] || '',
            featured_image: null,
            document_url: null,
            category: event.category || '',
            registration_url: event.registration_url || '',
            max_participants: event.max_participants || '',
            status: event.status,
            is_active: event.is_active
        });
        
        if (event.featured_image) {
            setImagePreview(`${MEDIA_URL}${event.featured_image}`);
        }
        if (event.document_url) {
            setDocumentPreview(event.document_url);
        }
        
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar este evento?')) return;

        try {
            await eventsService.delete(id);
            toast.success('Evento eliminado');
            loadEvents();
        } catch (error) {
            toast.error('Error al eliminar evento');
            console.error(error);
        }
    };

    const resetForm = () => {
        setFormData({
            title_es: '',
            description_es: '',
            location_es: '',
            event_date: '',
            event_time: '',
            end_date: '',
            featured_image: null,
            document_url: null,
            category: '',
            registration_url: '',
            max_participants: '',
            status: 'upcoming',
            is_active: true
        });
        setEditingEvent(null);
        setImagePreview(null);
        setDocumentPreview(null);
    };

    // Detectar si el evento está vencido basado en la fecha
    const parseLocalDate = (dateStr) => {
        if (!dateStr) return null;
        const fechaSolo = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
        const [year, month, day] = fechaSolo.split('-');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0);
    };

    const getStatusBadge = (event) => {
        // Si el evento está cancelado, mostrar ese estado
        if (event.status === 'cancelled') {
            return <span className="badge badge-error">Cancelado</span>;
        }

        // Verificar si el evento ya pasó (vencido)
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        
        const fechaEvento = parseLocalDate(event.event_date);
        const fechaFin = event.end_date ? parseLocalDate(event.end_date) : fechaEvento;

        // Si el evento ya pasó y no está marcado como completado, mostrar "Vencido"
        if (hoy > fechaFin && event.status !== 'completed') {
            return <span className="badge badge-warning">Vencido</span>;
        }

        // Mostrar el estado según el campo status
        const badges = {
            upcoming: { text: 'Próximo', class: 'badge-success' },
            ongoing: { text: 'En curso', class: 'badge-success' },
            completed: { text: 'Finalizado', class: 'badge-warning' }
        };
        const badge = badges[event.status] || badges.upcoming;
        return <span className={`badge ${badge.class}`}>{badge.text}</span>;
    };

    if (loading && events.length === 0) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando eventos...</p>
            </div>
        );
    }

    return (
        <div className="content-manage">
            <div className="content-header">
                <div className="header-info">
                    <h1>Gestión de Eventos</h1>
                    <p>Crea y administra los eventos de la federación</p>
                </div>
                <button className="btn btn-primary" onClick={() => {
                    resetForm();
                    setShowModal(true);
                }}>
                    <HiOutlinePlus />
                    <span>Nuevo Evento</span>
                </button>
            </div>

            {events.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <HiOutlineCalendar />
                    </div>
                    <h3>No hay eventos registrados</h3>
                    <p>Comienza creando tu primer evento</p>
                    <button className="btn btn-primary" onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}>
                        <HiOutlinePlus />
                        <span>Crear Evento</span>
                    </button>
                </div>
            ) : (
                <div className="events-grid">
                    {events.map(event => (
                        <div key={event.id} className="event-card card">
                            {event.featured_image && (
                                <div className="event-image-container">
                                    <img 
                                        src={`${MEDIA_URL}${event.featured_image}`} 
                                        alt={event.title_es}
                                        className="event-image"
                                    />
                                </div>
                            )}
                            <div className="card-body">
                                <div className="event-header-row">
                                    <h3 className="event-title">{event.title_es}</h3>
                                    {getStatusBadge(event)}
                                </div>
                                <p className="event-description">{event.description_es}</p>
                                
                                <div className="event-details">
                                    <div className="detail-item">
                                        <HiOutlineCalendar />
                                        <span>{new Date(event.event_date).toLocaleDateString('es-ES', { 
                                            day: '2-digit', 
                                            month: 'long', 
                                            year: 'numeric' 
                                        })}</span>
                                    </div>
                                    {event.event_time && (
                                        <div className="detail-item">
                                            <HiOutlineClock />
                                            <span>{event.event_time}</span>
                                        </div>
                                    )}
                                    {event.location_es && (
                                        <div className="detail-item">
                                            <HiOutlineLocationMarker />
                                            <span>{event.location_es}</span>
                                        </div>
                                    )}
                                    {event.max_participants && (
                                        <div className="detail-item">
                                            <HiOutlineUserGroup />
                                            <span>Máx. {event.max_participants} participantes</span>
                                        </div>
                                    )}
                                </div>

                                <div className="event-actions">
                                    <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(event)}>
                                        <HiOutlinePencil />
                                        <span>Editar</span>
                                    </button>
                                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(event.id)}>
                                        <HiOutlineTrash />
                                        <span>Eliminar</span>
                                    </button>
                                    {event.document_url && (
                                        <a 
                                            href={`${MEDIA_URL}${event.document_url}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-secondary"
                                        >
                                            <HiOutlineDocument />
                                            <span>Ver Documento</span>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">
                                {editingEvent ? 'Editar Evento' : 'Nuevo Evento'}
                            </h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <HiOutlineX />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="alert-info">
                                    <HiOutlineInformationCircle />
                                    <span>Solo escribe en español. La traducción al inglés se hará automáticamente.</span>
                                </div>

                                <div className="grid grid-cols-2">
                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">Título (Español) *</label>
                                        <input
                                            type="text"
                                            name="title_es"
                                            className="form-control"
                                            value={formData.title_es}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Ej: Campeonato Nacional de Natación 2026"
                                        />
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">Descripción (Español) *</label>
                                        <textarea
                                            name="description_es"
                                            className="form-control"
                                            value={formData.description_es}
                                            onChange={handleInputChange}
                                            required
                                            rows="4"
                                            placeholder="Describe el evento..."
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Fecha del Evento *</label>
                                        <input
                                            type="date"
                                            name="event_date"
                                            className="form-control"
                                            value={formData.event_date}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Hora</label>
                                        <input
                                            type="time"
                                            name="event_time"
                                            className="form-control"
                                            value={formData.event_time}
                                            onChange={handleInputChange}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Fecha de Finalización</label>
                                        <input
                                            type="date"
                                            name="end_date"
                                            className="form-control"
                                            value={formData.end_date}
                                            onChange={handleInputChange}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Ubicación (Español)</label>
                                        <input
                                            type="text"
                                            name="location_es"
                                            className="form-control"
                                            value={formData.location_es}
                                            onChange={handleInputChange}
                                            placeholder="Ej: Alberca Olímpica Ciudad de México"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Categoría</label>
                                        <input
                                            type="text"
                                            name="category"
                                            className="form-control"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                            placeholder="Ej: Natación, Clavados"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Máximo de Participantes</label>
                                        <input
                                            type="number"
                                            name="max_participants"
                                            className="form-control"
                                            value={formData.max_participants}
                                            onChange={handleInputChange}
                                            placeholder="Ej: 200"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">URL de Registro</label>
                                        <input
                                            type="url"
                                            name="registration_url"
                                            className="form-control"
                                            value={formData.registration_url}
                                            onChange={handleInputChange}
                                            placeholder="https://..."
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Estado *</label>
                                        <select
                                            name="status"
                                            className="form-control"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="upcoming">Próximo</option>
                                            <option value="ongoing">En curso</option>
                                            <option value="completed">Finalizado</option>
                                            <option value="cancelled">Cancelado</option>
                                        </select>
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">Imagen Destacada</label>
                                        <div className="file-input-wrapper">
                                            <input
                                                type="file"
                                                name="featured_image"
                                                className="file-input"
                                                onChange={handleFileChange}
                                                accept="image/*"
                                                id="event-image-input"
                                            />
                                            <label htmlFor="event-image-input" className="file-input-label">
                                                <HiOutlineDocument />
                                                <span>Seleccionar imagen del evento</span>
                                            </label>
                                        </div>
                                        {imagePreview && (
                                            <div className="image-preview">
                                                <img src={imagePreview} alt="Preview" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">Documento PDF/Archivo</label>
                                        <div className="file-input-wrapper">
                                            <input
                                                type="file"
                                                name="document_url"
                                                className="file-input"
                                                onChange={handleFileChange}
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                id="event-document-input"
                                            />
                                            <label htmlFor="event-document-input" className="file-input-label">
                                                <HiOutlineDocument />
                                                <span>Seleccionar documento (PDF, Word, Imagen)</span>
                                            </label>
                                        </div>
                                        {documentPreview && (
                                            <div className="file-preview">
                                                <HiOutlineDocument />
                                                <span>{documentPreview}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-check">
                                            <input
                                                type="checkbox"
                                                name="is_active"
                                                className="form-check-input"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                            />
                                            <span>Evento activo (visible en el sitio)</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                                            <span>Guardando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <HiOutlineCheckCircle />
                                            <span>{editingEvent ? 'Actualizar' : 'Crear Evento'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventsManage;
